import { makeObservable, observable, computed, reaction } from "mobx";
import { utils } from 'ynf-core-middleware'
import { closeWin } from '../../actions/navigate'
import { getYMSHost } from '@utils';

interface WorkFlowStoreProps {
  isTable?: boolean
  rootStore?: any
  formStore?: any
  rowStore?: any
}

export class WorkFlowStore {
  // store类型
  storeType: string = 'WorkFlowStore';
  // 列表页还是详情页
  isTable: boolean;
  // rootStore
  rootStore: any;
  // 详情页是formStore
  formStore: any;
  // 列表页是rowStore
  rowStore: any;
  // 流程实例
  flowInstance: any;
  // 流程按钮
  actionBtns: any;
  // 流程图地址
  detailUrl: string;
  // 流程权限
  fieldAuth: any;
  // 是否是最新状态
  latestStatus: boolean;
  // 审批中心js资源加载完毕
  flowJsLoaded: boolean;
  // 是否不执行refresh workflow
  noNeedRefreshWorkflow: boolean;
  // 审批流程是否加载失败
  flowIsError: boolean;
  // 记录注册事件(事件通过formStore注册)
  hooks: any;
  // 记录billId
  currentBillId: string;

  constructor(props: WorkFlowStoreProps) {
    const { isTable, rootStore, formStore, rowStore } = props;
    this.isTable = isTable || false
    this.rootStore = rootStore
    this.formStore = formStore
    this.rowStore = rowStore
    // store里的属性
    this.flowInstance = null
    this.actionBtns = []
    this.detailUrl = ''
    this.fieldAuth = null
    this.latestStatus = false
    this.flowJsLoaded = false
    this.noNeedRefreshWorkflow = false
    this.flowIsError = false
    this.currentBillId = ""
    this.hooks = this.formStore?.getWorkFlowHooks?.()

    makeObservable(this, {
      flowInstance: observable,
      fieldAuth: observable,
      detailUrl: observable,
      latestStatus: observable,
      flowJsLoaded: observable,
      flowIsError: observable,
      currentTaskAuth: computed
    })

    // 页面刷新，更新store
    reaction(() => this.rootStore.pageStore.pageRefresh, () => {
      const { pageStore = {} } = this.rootStore
      const { query = {}, params } = pageStore;
      const billId = this.isTable ? this.rowStore.getValue('id') : params.billId
      // 页面刷新，billId不一致时，需要重新初始化审批信息
      if (this.currentBillId && this.currentBillId !== billId) {
        this.flowInstance = null
        this.latestStatus = false
        this.init()
      } else {
        if (query.terminalType === '3') {
          this.latestStatus && this.updateStore()
        } else {
          this.updateStore()
        }
      }
    })

    // 详情页审批状态变化，更新store
    reaction(() => this.formStore?.getValue('verifyState'), () => {
      this.flowJsLoaded && this.updateStore()
    })

    this.loadFlowJS()
  }

  init() {
    // 初始化审批前事件
    this.hooks?.initWorkFlow?.(this)
    if (this.flowInstance) return
    const { pageStore } = this.rootStore
    const { params } = pageStore
    const that = this
    const billId = this.isTable ? this.rowStore.getValue('id') : params.billId
    this.currentBillId = billId
    // 没有业务对象或者单据id的话，不初始化
    if (!params?.busiObj || !billId) {
      return
    }
    // 列表页verifyState审批状态是0的话，不初始化
    if (this.isTable && !this.rowStore.getValue('verifyState')) {
      return
    }
    // 详情页verifyState审批状态是0的话，不初始化
    if (!this.isTable && !this.formStore.getValue('verifyState')) {
      return
    }
    const flowParam = {
      appsource: params?.domainKey,
      businessKey: `${params?.busiObj}_${billId}`,
      bpmHost: window.flowCompParams?.host,
      sourceType: 'historyInline',
      lang: this.rootStore.context?.locale?.replace('_', '-') || 'zh-CN',
      tenantId: params?.tenantId || this.rootStore.context?.tenantid,
      userId: this.rootStore.context?.userid,
      isActionAndHistory: true,
      isNoHistory: false,
      newReform: true,
      serviceCode: params.serviceCode || '',
      formDetailUrl: this.getDetailUrl('web'), // 发出的pc端待办的地址，pc端读的是formDetailUrl属性
      formUrl: this.getDetailUrl('web'), // 发出的pc端待办的地址，移动端读的是formUrl属性
      mobileUrl: this.getDetailUrl('mobile'), // 发出的移动端的待办地址
      a01: params?.['x-a01'],
      confirmRequest: (flag: boolean) => {
        if (flag) {
          that.actionBtns = that.flowInstance.getWorkFlowActionBtns?.() || [] // pc端没有getWorkFlowActionBtns方法
          that.detailUrl = that.flowInstance.getWorkFlowDetailUrl?.()
          that.fieldAuth = that.flowInstance.getWorkFlowFieldAuth?.().fieldAuth
          that.latestStatus = true
        }
      },
      isMDF: true,
      // 有审批信息，无审批流时会调用
      errCallBack: (errMsg: any) => {
        that.flowIsError = true
      },
      // 移动审批等按钮点击前事件
      onBeforeAction:(data: any = {}) => {
        // 审批等按钮点击前事件
        if (that.hooks?.onBeforeAction) {
          return that.hooks.onBeforeAction(data);
        }
        return new Promise((resolve) => {
          resolve(true);
        });
      }
    }
    console.log('workflowStore的流程初始化参数', flowParam)
    this.flowInstance = window.flowComp(flowParam)
    this.flowInstance.showFlowBox({
      // 移动端点击按钮后的回调函数
      onClose: (data: any) => {
        if (that.isTable) {
          that.rootStore.pageStore.bNeedRefresh = true
        } else {
          that.noNeedRefreshWorkflow = data.noNeedRefreshWorkflow
          that.updateVerifyState(data)
        }
        if (that.hooks?.onCloseAction) {
          that.hooks.onCloseAction(data);
          return
        }
        if (data.processStatus) {
          that.closeWindow()
        }
      },
      onBeforeAction:(data: any = {}) => {
        // 审批等按钮点击前事件
        if (that.hooks?.onBeforeAction) {
          return that.hooks.onBeforeAction(data);
        }
        return new Promise((resolve) => {
          resolve(true);
        });
      },
      // pc端点击按钮后的回调函数
      onAfterAction: (data: any = {}) => {
        if (that.isTable) {
          that.rootStore.pageStore.bNeedRefresh = true
        } else {
          that.updateVerifyState(data)
        }
        // 审批等按钮点击后事件， 后续逻辑不继续执行
        if (that.hooks?.onAfterAction) {
          that.hooks.onAfterAction(data)
          return
        }
        that.closeWindow() 
      }
    })
  }

  // 加载审批中心js
  async loadFlowJS() {
    if (!window.flowComp) {
      const hostUrl = getYMSHost()
      const terminalType = this.rootStore.pageStore.params?.terminalType || '3'
      const url = `${hostUrl}/iuap-apcom-workflownew/approve-component/init/${terminalType === '1' ? 'loadWebJs' : 'loadJs'}`
      await utils.loadScript(url)
    }
    const timer = setInterval(() => {
      if (window.flowComp?.isReady) {
        clearInterval(timer)
        this.flowJsLoaded = true
      }
    }, 100)
  }

  // 页面刷新或者单据的审批状态变化时，更新store
  updateStore() {
    // 详情页需要刷新flowInstance，列表页清空flowInstance
    const verifyState = this.formStore?.getValue('verifyState')
    if (verifyState && this.flowInstance) {
      this.latestStatus = false
      if (!this.noNeedRefreshWorkflow) {
        this.flowInstance.refreshWorkflow()
      }
    } else if (verifyState) {
      // 审批流存在的情况下进行初始化
      if (this.formStore.procinstId){
        this.init()
      }  
    } else {
      this.flowInstance = null
      this.actionBtns = []
      this.detailUrl = ''
      this.fieldAuth = null
      this.latestStatus = false
    }
  }

  // 获取审批中心需要的pc和移动url地址
  getDetailUrl(type = 'web') {
    const { pageStore } = this.rootStore
    let billNo = pageStore.params.busiObj
    const filteredObj = Object.fromEntries(
      Object.entries(pageStore.params).filter(([key, value]) => value !== "" && value != null)
    );
    if (type === 'mobile') {
      billNo = billNo + 'MobileArchive'
      filteredObj.terminalType = 3
    } else {
      filteredObj.terminalType = 1
    }
    const search = new URLSearchParams(filteredObj).toString();
    const hostUrl = getYMSHost()
    return `${hostUrl}/mdf-node/fragment/${billNo}?${search}`
  }

  // 单据详情页更新页面状态
  updateVerifyState(data: any = {}) {
    const status = {
      IN_APPROVAL: 1,
      APPROVED: 2,
      APPROVAL_TERMINATION: 3,
      OPEN_APPROVAL: 4
    }
    if (data.processStatus) {
      const verifyState = status[data.processStatus] || -1
      if (verifyState >= 0) {
        this.formStore?.get('verifyState')?.setValue(verifyState)
        this.rootStore.pageStore.bNeedRefresh = true
      }
    }
  }

  // 移动页面旧版审批组件调用方式，直接调用此方法可以调出旧版弹窗
  showOldFlow(el: string) {
    this.hooks?.initWorkFlow?.(this)
    const { pageStore } = this.rootStore
    const { params } = pageStore
    const billId = this.isTable ? this.rowStore.getValue('id') : params.billId
    const flowParam: any = {
      el,
      appsource: params?.domainKey,
      businessKey: `${params?.busiObj}_${billId}`,
      bpmHost: window.flowCompParams?.host,
      sourceType: 'action',
      lang: this.rootStore.context?.locale?.replace('_', '-') || 'zh-CN',
      tenantId: params?.tenantId || this.rootStore.context?.tenantid,
      userId: this.rootStore.context?.userid,
      showHistory: true,
      serviceCode: params.serviceCode || '',
      formDetailUrl: this.getDetailUrl('web'), // 发出的pc端待办的地址，pc端读的是formDetailUrl属性
      formUrl: this.getDetailUrl('web'), // 发出的pc端待办的地址，移动端读的是formUrl属性
      mobileUrl: this.getDetailUrl('mobile'), // 发出的移动端的待办地址
      a01: params?.['x-a01'],
      // 移动审批等按钮点击前事件
      onBeforeAction:(data: any = {}) => {
        // 审批等按钮点击前事件
        if (that.hooks?.onBeforeAction) {
          return that.hooks.onBeforeAction(data);
        }
        return new Promise((resolve) => {
          resolve(true);
        });
      }
    }
    const that = this
    window.flowComp(flowParam).showFlowBox({
      // 移动端点击按钮后的回调函数
      onClose: (data: any) => {
        if (that.isTable) {
          that.rootStore.pageStore.bNeedRefresh = true
        } else {
          that.updateVerifyState(data)
        }
        if (that.hooks?.onCloseAction) {
          that.hooks.onCloseAction(data);
          return
        }
        if (data.processStatus) {
          that.closeWindow()
        }
      },
      // pc端点击按钮后的回调函数
      onAfterAction: (data: any = {}) => {
        that.closeWindow()
        if (that.isTable) {
          that.rootStore.pageStore.bNeedRefresh = true
        } else {
          that.updateVerifyState(data)
        }
      }
    })
  }

  // pc端发起指派
  renderAssigneeNew(assignData: any) {
    // 只在详情页调用
    if (this.isTable) return
    const { pageStore } = this.rootStore
    const { params } = pageStore
    const billId = this.isTable ? this.rowStore.getValue('id') : params.billId
    return new Promise((resolve, reject) => {
      const flowParam = {
        width: 850,
        height: 510,
        businessKey: `${params?.busiObj}_${billId}`,
        model: 'assign',
        appsource: params?.domainKey,
        tenantId: params?.tenantId || this.rootStore.context?.tenantid,
        userId: this.rootStore.context?.userid,
        lang: this.rootStore.context?.locale?.replace('_', '-') || 'zh-CN',
      }
      const flowIns = window.flowComp(flowParam)
      flowIns?.renderAssigneeNew({
        onClose: (callbackData: any) => {
          if (callbackData?.length) {
            resolve(callbackData)
          }
          reject(false)
        },
        assignData
      })
    })
  }

  // 移动端发起指派
  showAssignCheck(assignInfoData: any) {
    // 只在详情页调用
    if (this.isTable) return
    const { pageStore } = this.rootStore
    const { params } = pageStore
    return new Promise((resolve, reject) => {
      const assignParam = {
        appsource: params?.domainKey,
        lang: this.rootStore.context?.locale?.replace('_', '-') || 'zh-CN',
        assignInfoData,
        closeAssignCom: (callbackData: any) => {
          window.onInterceptBack = null
          if (callbackData?.length) {
            resolve(callbackData)
          }
          reject(false)
        },
        onClosePopup: () => {
          window.onInterceptBack = null
          reject(false)
        },
        businessKey: `${params?.busiObj}_${params?.billId}`,
      }
      let assIns = window.assignComp(assignParam)
      assIns.showAssignBox()
      window.onInterceptBack = () => {
        window.onInterceptBack = null
        assIns?.assignCompInstance?.hidePopup?.()
        assIns = null
        reject(false)
      }
    })
  }

  // 是否关闭当前页面
  closeWindow() {
    const { pageStore = {} } = this.rootStore
    const { query = {}, generalSetting = {} } = pageStore;
    if (query.adt !== 'wf') {
      return false
    }
    if (generalSetting.saveClose === 'true') {
      closeWin(this.rootStore, {})
      return true
    }
    return false;
  }

  get currentTaskAuth() {
    if (!this.fieldAuth) return
    const currentTask = this.fieldAuth.data?.find((item: any) => {
      return item.taskId === this.fieldAuth.currentTaskId
    })
    return currentTask?.activityAuthority?.activityContentJson
  }

  setTaskAuth(newAuth: any) {
    if (!this.currentTaskAuth) return
    const index = this.fieldAuth.data?.findIndex((item: any) => {
      return item.taskId === this.fieldAuth.currentTaskId
    })
    this.fieldAuth.data[index].activityAuthority.activityContentJson = newAuth
  }
}